/**
 * Report Service (NL2SQL)
 * Handles AI-powered report generation and management
 * Security: SQL is never exposed to frontend
 */
import BaseApiService from '../base/BaseApiService';
import type { NL2SQLConfig, NL2SQLMetadata, NL2SQLChartType } from '@app-types';

class ReportService extends BaseApiService {
  /**
   * Translate natural language query to SQL
   */
  async translateNaturalLanguageToSQL(query: string): Promise<{
    query_id: string;
    nl_query: string;
    explanation: string;
    chartType?: NL2SQLChartType;
    config?: NL2SQLConfig;
    metadata?: NL2SQLMetadata;
    visualization: {
      chartType: 'bar' | 'line' | 'pie' | 'table';
      title: string;
      suggested?: boolean;
    };
  }> {
    return this.post<{
      query_id: string;
      nl_query: string;
      explanation: string;
      chartType?: NL2SQLChartType;
      config?: NL2SQLConfig;
      metadata?: NL2SQLMetadata;
      visualization: {
        chartType: 'bar' | 'line' | 'pie' | 'table';
        title: string;
        suggested?: boolean;
      };
    }>('/nl2sql/translate', { query });
  }

  /**
   * Execute SQL by widget_id - SQL is retrieved from DB, not from frontend
   */
  async executeWidgetSQL(widgetId: number): Promise<{
    results: Record<string, unknown>[];
    row_count: number;
    chartType?: NL2SQLChartType;
    config?: NL2SQLConfig;
    metadata?: NL2SQLMetadata;
    visualization: {
      chartType: 'bar' | 'line' | 'pie' | 'table';
      title: string;
    };
  }> {
    return this.post<{
      results: Record<string, unknown>[];
      row_count: number;
      chartType?: NL2SQLChartType;
      config?: NL2SQLConfig;
      metadata?: NL2SQLMetadata;
      visualization: {
        chartType: 'bar' | 'line' | 'pie' | 'table';
        title: string;
      };
    }>('/nl2sql/execute', { widget_id: widgetId });
  }

  /**
   * Get all report widgets - sql_query is NOT included in response for security
   */
  async getReportWidgets(): Promise<{
    widgets: Array<{
      id: number;
      name: string;
      visualization_type: NL2SQLChartType;
      config?: NL2SQLConfig;
      metadata?: NL2SQLMetadata;
      created_at: string;
    }>;
  }> {
    return this.get<{
      widgets: Array<{
        id: number;
        name: string;
        visualization_type: NL2SQLChartType;
        config?: NL2SQLConfig;
        metadata?: NL2SQLMetadata;
        created_at: string;
      }>;
    }>('/nl2sql/widgets');
  }

  /**
   * Create widget using query_id - SQL is retrieved from server cache
   */
  async createReportWidget(widget: {
    name: string;
    query_id: string;
    visualization_type: NL2SQLChartType;
    config?: NL2SQLConfig;
    metadata?: NL2SQLMetadata;
  }): Promise<{
    widget: {
      id: number;
      name: string;
      visualization_type: NL2SQLChartType;
      config?: NL2SQLConfig;
      metadata?: NL2SQLMetadata;
      created_at: string;
    };
  }> {
    return this.post<{
      widget: {
        id: number;
        name: string;
        visualization_type: NL2SQLChartType;
        config?: NL2SQLConfig;
        metadata?: NL2SQLMetadata;
        created_at: string;
      };
    }>('/nl2sql/widgets', widget);
  }

  /**
   * Delete a report widget
   */
  async deleteReportWidget(widgetId: number): Promise<{
    success: boolean;
  }> {
    return this.delete<{
      success: boolean;
    }>(`/nl2sql/widgets/${widgetId}`);
  }

  /**
   * Get NL2SQL schema information
   */
  async getNL2SQLSchema(): Promise<{
    tables: Array<{
      name: string;
      description: string;
    }>;
    examples: string[];
  }> {
    return this.get<{
      tables: Array<{
        name: string;
        description: string;
      }>;
      examples: string[];
    }>('/nl2sql/schema');
  }
}

export const reportService = new ReportService();
export default reportService;
